import type { MenuContentProps, MenuRootProps } from '@chakra-ui/react';
import { Box, Menu, Portal, Flex } from '@chakra-ui/react';
import { Fragment } from 'react';
import { ContextMenuContext } from './context';
import type { ContextMenuItemProps, CustomRender } from './types';

export type ContextMenuProps<T> = {
  children: React.ReactNode;
  contentProps?: MenuContentProps;
  rootProps?: Omit<MenuRootProps, 'children'>;
  items?: ContextMenuItemProps<T>[];
  itemsGetter?: (data: T) => ContextMenuItemProps<T>[] | Promise<ContextMenuItemProps<T>[]>;
};

export function ContextMenu<T>({
  items,
  itemsGetter,
  children,
  contentProps,
  rootProps,
}: ContextMenuProps<T>) {
  const [items_, setItems_] = useState<ContextMenuItemProps<T>[]>([]);
  const [topToolbars_, setTopToolbars_] = useState<
    Array<{ custom: CustomRender<T>; value: string }>
  >([]);
  const [data, setData] = useState<T>();

  const [visible, setVisible] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);

  const actions = useMemo(
    () => ({ switchVisible: setVisible, switchOpen: setOpen }),
    [setVisible, setOpen]
  );

  const value = useMemo(
    () => ({
      data,
      setData,
    }),
    [data]
  );

  const handleItems = useCallback((items: ContextMenuProps<T>['items']) => {
    const items_: ContextMenuItemProps<T>[] = [];
    const topToolbars_: Array<{ custom: CustomRender<T>; value: string }> = [];
    items?.forEach((item) => {
      if (item.hidden) return;
      if (item.isTopToolbar) {
        if (!item.custom) return;
        topToolbars_.push({ custom: item.custom, value: item.value });
        return;
      }
      items_.push(item);
    });
    setItems_(items_);
    setTopToolbars_(topToolbars_);
  }, []);

  useLayoutEffect(() => {
    if (!items) return;
    handleItems(items);
  }, [items, handleItems]);

  useEffect(() => {
    if (!open) return;
    if (data && itemsGetter) {
      Promise.resolve(itemsGetter(data)).then((items) => {
        handleItems(items);
      });
    }
  }, [open, data, itemsGetter, handleItems]);

  return (
    <Menu.Root
      lazyMount
      unmountOnExit
      {...rootProps}
      open={open}
      onOpenChange={(details) => {
        setOpen(details.open);
        if (!details.open) return;
        setVisible(true);
      }}
      onFocusOutside={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onPointerDownOutside={(e) => {
        const target = e.detail.target as HTMLElement;
        const closest = target.closest('[data-scope="menu"][data-part="content"]');
        if (!closest) return;
        e.preventDefault();
        e.stopPropagation();
      }}
      closeOnSelect={false}
      onSelect={({ value }) => {
        const onItemClick = items_.find((item) => item.value === value)?.onItemClick;
        if (!onItemClick) return;
        onItemClick(value, data);
        setOpen(false);
      }}
    >
      <ContextMenuContext.Provider value={value}>{children}</ContextMenuContext.Provider>
      <Portal>
        <Menu.Positioner>
          <Flex flexDirection="column-reverse" alignItems="center">
            <Menu.Content
              backdropFilter="blur(6px)"
              bg="whiteAlpha.70"
              display={visible && items_.length + topToolbars_.length > 0 ? 'block' : 'none'}
              {...contentProps}
            >
              {items_.map(({ danger, icon, label, description, onItemClick, custom, ...item }) =>
                custom ? (
                  <Fragment key={item.value}>{custom(data, actions)}</Fragment>
                ) : (
                  <Menu.Item
                    key={item.value}
                    {...item}
                    px="1"
                    py="3"
                    borderRadius="sm"
                    cursor="pointer"
                    color={danger ? 'fg.error' : 'fg'}
                    _hover={{ bg: danger ? 'bg.error' : 'bg.subtle' }}
                  >
                    {icon && <Box>{icon}</Box>}
                    <Box>
                      {label}
                      {description && <Box fontSize="xs">{description}</Box>}
                    </Box>
                  </Menu.Item>
                )
              )}
            </Menu.Content>
            {/* because Menu.Content register only once receive event, so we need to render topToolbars_ in reverse order */}
            {topToolbars_.map(({ custom, value }) => (
              <Fragment key={value}>{custom(data, actions)}</Fragment>
            ))}
          </Flex>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}
